import React from 'react';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import { LayoutChangeEvent, StyleSheet, Text, View } from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';
import { VolumePromotionDateLabel } from './volume-promotion-date-label';

interface Props {
  dateLabel: string;
  imageUri: string;
  title: string;
}

export const PromoImageCard: React.FC<Props> = ({
  imageUri,
  dateLabel,
  title,
}) => {
  const [styles] = useStyles();
  const { isSmallLayout } = useLayoutContext();

  const titleStyle = isSmallLayout ? TYPOGRAPHY.h3 : TYPOGRAPHY.pSemiBold;
  const [maxLines, setMaxLines] = React.useState<number | undefined>(undefined);

  // need this for ellipsis to appear correctly on mobile _and_ web (web doesn't show ellipsis when just using large numberOfLines)
  const handleTitleLayout = React.useCallback(
    (event: LayoutChangeEvent) => {
      const { height } = event.nativeEvent.layout;
      if (titleStyle.lineHeight) {
        const lines = Math.floor(height / titleStyle.lineHeight);
        if (lines > 0) {
          setMaxLines(lines);
        }
      }
    },
    [titleStyle.lineHeight],
  );

  return (
    <>
      <View style={styles.imageWrapper}>
        <CachedImage
          alt=""
          contentFit="cover"
          source={{ uri: imageUri }}
          style={[styles.image, StyleSheet.absoluteFillObject]}
        />
      </View>
      <View style={[styles.contentWrapper]}>
        <VolumePromotionDateLabel dateLabel={dateLabel} variant="pill" />
        <View onLayout={handleTitleLayout} style={styles.titleWrapper}>
          {maxLines && (
            <Text numberOfLines={maxLines} style={[titleStyle]}>
              {title}
            </Text>
          )}
        </View>
      </View>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  contentWrapper: {
    backgroundColor: theme.colors.white,
    flex: 1,
    gap: 8,
    height: '100%',
    maxWidth: '50%',
    padding: 16,
  },
  image: {
    height: '100%',
    width: '100%',
  },
  imageWrapper: {
    flex: 1,
    height: '100%',
    minHeight: 196,
    position: 'relative',
  },
  titleWrapper: {
    flex: 1,
    overflow: 'hidden',
  },
}));
